import type { ChangeEvent, ReactNode } from 'react';
import React from 'react';
import type { FormBlurEventFn } from '@noths/polaris-client-react-hooks';
import { CheckboxWithLabel, Feedback } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { RuleSet } from 'styled-components';

import * as groupStyles from 'src/components/molecules/styles/Group.styles';

export type CheckboxData = {
  children?: ReactNode;
  labelText: string;
  showChildren?: boolean;
  value: string;
};

export interface CheckboxGroupProps<TName extends string> {
  accessibleLabel?: string;
  additionalCss?: RuleSet<object>;
  checkboxData: CheckboxData[];
  children?: ReactNode;
  description?: string;
  error?: string | null;
  hideLabel?: boolean;
  name: TName;
  onBlur?: FormBlurEventFn;
  onChange: (value: string[], name: TName) => void;
  required?: boolean;
  value: string[];
}

export const getNewValueArray = (e: ChangeEvent<HTMLInputElement>, value: string[]) =>
  e.target.checked ? [...value, e.target.value] : value.filter((v) => v !== e.target.value);

export const CheckboxGroup = <TName extends string>({
  accessibleLabel,
  additionalCss,
  checkboxData,
  children,
  description,
  error,
  hideLabel,
  name,
  onBlur,
  onChange,
  required,
  value,
  ...inputProps
}: CheckboxGroupProps<TName>) => {
  return (
    <fieldset css={[groupStyles.borderless, additionalCss]} onBlur={onBlur} {...inputProps}>
      <legend
        css={hideLabel || !accessibleLabel ? visuallyHidden : undefined}
        id={`${name}-legend`}
      >
        {accessibleLabel || `${name}-legend`}
      </legend>
      {description && <p id={`${name}-description`}>{description}</p>}
      {checkboxData.map(({ children, labelText, showChildren, value: checkboxValue }) => (
        <React.Fragment key={checkboxValue}>
          <CheckboxWithLabel
            checked={value.includes(checkboxValue)}
            css={groupStyles.checkboxWithLabel}
            key={checkboxValue}
            label={labelText}
            name={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const newValueArray = getNewValueArray(e, value);
              onChange(newValueArray, name);
            }}
            required={required}
            value={checkboxValue}
          />
          {showChildren && children}
        </React.Fragment>
      ))}
      {children}
      {error && (
        <div css={groupStyles.fieldFeedbackContainer} role="alert">
          <Feedback error iconAlign="left" id={`${name}-error`} name={error} />
        </div>
      )}
    </fieldset>
  );
};
